import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Loader2, Plus, Minus, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";

/** Log a completed workout — one set-list per exercise. Prefills from the last log
 *  so returning users just tick reps up or bump the weight. */
export function WorkoutLogModal({ planId, task, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Build initial items from the task's prescription; then hydrate from last log.
    const initial = (task.exercises || []).map((e) => ({
      exercise_id: e.exercise_id,
      name: e.name,
      sets: Array.from({ length: Math.max(1, Number(e.sets) || 3) }, () => ({
        weight: "",
        reps: parseInt(String(e.reps).match(/\d+/)?.[0] || "10", 10),
      })),
    }));
    setItems(initial);

    (async () => {
      try {
        const { data } = await api.get(
          `/coach/plans/${planId}/tasks/${task.id}/last-log`
        );
        const last = data.log;
        if (last?.exercises?.length) {
          setItems((prev) =>
            prev.map((it) => {
              const match = last.exercises.find(
                (x) => x.exercise_id === it.exercise_id
              );
              if (!match) return it;
              return {
                ...it,
                sets: match.sets.map((s) => ({
                  weight: s.weight ?? "",
                  reps: s.reps ?? it.sets[0]?.reps ?? 10,
                })),
              };
            })
          );
        }
      } catch {
        /* ignore — no prior log */
      } finally {
        setLoading(false);
      }
    })();
  }, [planId, task]);

  const updateSet = (exIdx, setIdx, field, value) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== exIdx) return it;
        const sets = it.sets.map((s, j) => (j === setIdx ? { ...s, [field]: value } : s));
        return { ...it, sets };
      })
    );
  };

  const addSet = (exIdx) =>
    setItems((prev) =>
      prev.map((it, i) =>
        i === exIdx
          ? {
              ...it,
              sets: [
                ...it.sets,
                {
                  weight: it.sets.at(-1)?.weight || "",
                  reps: it.sets.at(-1)?.reps || 10,
                },
              ],
            }
          : it
      )
    );

  const removeSet = (exIdx, setIdx) =>
    setItems((prev) =>
      prev.map((it, i) =>
        i === exIdx
          ? { ...it, sets: it.sets.filter((_, j) => j !== setIdx) }
          : it
      )
    );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        exercises: items.map((it) => ({
          exercise_id: it.exercise_id,
          name: it.name,
          sets: it.sets.map((s) => ({
            weight: s.weight === "" ? null : Number(s.weight),
            reps: Number(s.reps) || 0,
          })),
        })),
        notes: notes || null,
      };
      const { data } = await api.post(
        `/coach/plans/${planId}/tasks/${task.id}/log`,
        payload
      );
      toast.success("Workout logged. Great job!");
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      onClick={onClose}
      data-testid="workout-log-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-5 sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C2410C]">
              Log your workout
            </p>
            <h3 className="mt-1 font-nunito text-lg font-bold text-[#2D3748]">{task.title}</h3>
            <p className="mt-0.5 font-quicksand text-xs text-[#718096]">
              Tap ➕ to add a set. Weight in lb (leave blank for bodyweight).
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-[#718096] hover:bg-[#FFF9F5]"
            data-testid="close-log-modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-[#FF7E67]" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center font-quicksand text-sm text-[#718096]">
            No exercises defined on this task — log freeform notes below.
          </p>
        ) : (
          <div className="space-y-4">
            {items.map((it, exIdx) => (
              <div
                key={`${it.exercise_id}-${exIdx}`}
                className="rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] p-3"
                data-testid={`log-exercise-${it.exercise_id}`}
              >
                <p className="font-nunito text-sm font-bold text-[#2D3748]">{it.name}</p>
                <div className="mt-2 space-y-1.5">
                  {it.sets.map((s, sIdx) => (
                    <div key={sIdx} className="flex items-center gap-2">
                      <span className="w-8 font-nunito text-xs font-bold text-[#718096]">
                        #{sIdx + 1}
                      </span>
                      <input
                        type="number"
                        step="0.5"
                        inputMode="decimal"
                        placeholder="wt (lb)"
                        value={s.weight}
                        onChange={(e) => updateSet(exIdx, sIdx, "weight", e.target.value)}
                        data-testid={`weight-${exIdx}-${sIdx}`}
                        className="h-9 w-24 rounded-full border border-[#FFE8E3] bg-white px-3 text-sm outline-none focus:border-[#FF7E67]"
                      />
                      <span className="text-xs text-[#718096]">×</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        placeholder="reps"
                        value={s.reps}
                        onChange={(e) => updateSet(exIdx, sIdx, "reps", e.target.value)}
                        data-testid={`reps-${exIdx}-${sIdx}`}
                        className="h-9 w-20 rounded-full border border-[#FFE8E3] bg-white px-3 text-sm outline-none focus:border-[#FF7E67]"
                      />
                      <span className="text-xs text-[#718096]">reps</span>
                      <button
                        onClick={() => removeSet(exIdx, sIdx)}
                        className="ml-auto rounded-full p-1 text-[#A0AEC0] hover:bg-white hover:text-[#FF7E67]"
                        aria-label="Remove set"
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addSet(exIdx)}
                    className="mt-1 inline-flex items-center gap-1 rounded-full border border-[#FFE8E3] bg-white px-3 py-1 font-nunito text-[11px] font-bold text-[#C2410C] hover:bg-[#FFF4EF]"
                    data-testid={`add-set-${exIdx}`}
                  >
                    <Plus className="h-3 w-3" /> Add set
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="How did it feel? Anything to note for next week? (optional)"
          rows={2}
          className="mt-4 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2 font-quicksand text-sm outline-none focus:border-[#FF7E67]"
          data-testid="log-notes"
        />

        <button
          onClick={save}
          disabled={saving}
          data-testid="save-log-btn"
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-sunrise py-3 font-nunito text-sm font-bold text-white shadow-glow-coral disabled:opacity-60"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Saving…
            </>
          ) : (
            <>
              <Save className="h-4 w-4" /> Save workout
            </>
          )}
        </button>
      </motion.div>
    </div>,
    document.body
  );
}
